import { randomUUID } from "crypto";
import { existsSync } from "fs";
import { basename } from "path";
import { buildFlagArgs, buildSettingsJson } from "./claude-flags";
import { getLogger, type Logger } from "./logger";
import { clearClaudeExeCache, resolveClaudeExe } from "./platform";
import { CLAUDE_CODE_PROVIDER } from "./providers";
import { createScreen, type InternalSession, loadPty, PTY_ROWS, stripAnsi } from "./pty-shared";
import {
  detectGateScreen,
  detectPickerScreen,
  hasPermissionOsc,
  hasWaitingForInputOsc,
  type PermissionGate,
  permissionContentKey,
  scrapePermissionGate,
} from "./services/questions/detectPermissionGate";
import { readPromptSuggestion } from "./services/questions/detectPromptSuggestion";
import {
  detectQuestionFromScreen,
  questionContentKey,
} from "./services/questions/detectQuestionFromScreen";
import { detectShellPrompt } from "./services/questions/detectShellPrompt";
import { detectStartupChoiceGate } from "./services/questions/detectStartupChoiceGate";
import { parseAgentPhase } from "./services/questions/parseAgentPhase";
import { isPermissionAnswer } from "./services/questions/permissionAnswerKeys";
import type {
  AgentPhase,
  ManagedSession,
  PTYManagerOptions,
  SessionRunner,
  StartFreshSessionOptions,
  StartSessionOptions,
  StatusSource,
  UserMessage,
} from "./types";
import { debounce } from "./utils/debounce";
import { deriveSessionName } from "./utils/deriveSessionName";

const OUTPUT_BUFFER_MAX = 65536;

// Cap on recorded user messages per session (drop oldest). Bounds memory on a
// long-lived session; replay only needs the recent tail for ownership.
const INPUT_HISTORY_MAX = 50;

// PTY geometry, the render terminal, node-pty loading, the session shape and
// ANSI stripping are shared with codex-pty-runner.ts — see pty-shared.ts.

// Markers that indicate Claude has reached an interactive prompt and is ready
// for user input. The TUI has at least two startup variants:
//   - "Tips" banner: renders a box with corner ╭ characters
//   - Connector/MCP-status splash: no box at all; shows ❯ as the prompt arrow
// We treat either marker in a chunk as "ready". False-positives are harmless —
// the worst case is flushing queued input slightly early, which Claude buffers.
const CLAUDE_PROMPT_MARKERS = ["╭", "❯"] as const;

// Claude's turn signal: OSC 9;4 (terminal progress) — `9;4;3` when a turn
// starts, `9;4;0` exactly when it ends, including across a permission gate
// (verified on Claude Code v2.1.278). Mid-conversation the `❯` input box stays
// painted for the whole turn, so the marker alone flipped the session to
// waiting_input ~50ms after every submit and the "waiting for your input" push
// fired as the agent started. While a turn is open the markers are ignored.
// biome-ignore lint/suspicious/noControlCharactersInRegex: matching the literal OSC 9;4 escape
const CLAUDE_PROGRESS_RE = /\x1b\]9;4;(\d)/g;

// A submit opens the turn before Claude's `9;4;3` arrives (~300ms later),
// because the echo of the pasted prompt already carries `❯`. A submit that
// starts no turn at all (a slash command) never sends one, so the optimistic
// open lapses after this long and readiness is re-checked from the screen.
const TURN_START_GRACE_MS = 3_000;

// Re-run ready/prompt detection this long after the PTY goes quiet, instead of
// waiting for another chunk (which may never come if Claude is blocked on a
// prompt) or the full CLAUDE_READY_FALLBACK_MS. 500ms was picked from real
// [pty.chunk] gap logs: p50/p75 inter-chunk gaps while status=running are
// 31ms/467ms, so 500ms clears normal streaming pauses without adding
// meaningful perceived latency (see docs/postmortems or session notes for the
// gapMs sample this was based on).
const QUIET_DETECT_MS = 500;

// Raw tail carried across chunks for the OSC 777 regexes. node-pty chunk
// boundaries are arbitrary, and the ~54-char notify escape matched NEITHER
// half when split. 128 chars covers the escape with margin, tmux-wrapped or
// not.
const OSC_TAIL_CHARS = 128;

// Ceiling on unsolicited rendered-screen scrapes: a chunk's arrival alone
// may trigger a full detection pass at most this often. Exists because the
// gate paint has NO reliable chunk-level signal (the TUI paints
// cursor-addressed fragments and the box gutter defeats the hint regex)
// while Claude debounces its OSC 777 notify ~6s — see
// docs/superpowers/specs/2026-08-08-paint-time-gate-detection-design.md.
// 300ms = half the blink cadence of a waiting gate (~450-620ms observed), so
// worst-case added card latency is one tick and scrape work is ≤ ~3/s per
// active session (idle sessions emit no chunks, so they never scrape).
const SCRAPE_THROTTLE_MS = 300;

// Flat backstop armed at spawn, mirroring CODEX_READY_FALLBACK_MS. It replaces
// a chunk-gated 10s check that could only fire if another chunk happened to
// arrive — useless for the case it was written for, a boot that falls silent
// without ever painting a marker. A timer fires either way.
//
// The quiet-checker cannot fill this role: a resume replaying a large JSONL is
// quiet for well over QUIET_DETECT_MS *before* the TUI reads stdin, so treating
// that silence as readiness disarms the input queue during exactly the window
// it exists to protect. Must stay below server.ts START_READY_TIMEOUT_MS (10s)
// so a start request resolves on our verdict rather than racing its timeout.
const CLAUDE_READY_FALLBACK_MS = 8_000;

// Build the two byte sequences for a paste-then-submit. We deliberately split
// the paste body and the trailing \r into two separate PTY writes (see
// writeSubmit() below) so Claude's TUI gets one event-loop tick to process the
// paste (clear input buffer, render `Pasting…`) before Enter arrives.
//
// Why bracketed paste at all: Claude's TUI enables bracketed paste mode
// (\x1b[?2004h) at startup. Content between \x1b[200~ and \x1b[201~ is
// committed as a single insertion without triggering autocomplete or key
// bindings. Without this wrap an input like "@<path>" opens the mention
// picker and the trailing \r gets consumed as "accept completion" rather
// than "submit" — see docs/postmortems/2026-05-20-pty-bracketed-paste-fix.md.
//
// Why split paste and \r: on 2026-05-27 a follow-up stuck session
// (39118d3e) showed the bracketed-paste wrap was being written but the
// trailing \r still didn't submit — Claude's TUI was mid-render of a
// startup status banner ("Update available", "192 skill descriptions
// dropped", etc.) when the bytes arrived, and the whole chunk landed in
// the wrong handler context. Splitting the write lets the TUI ingest the
// paste in one tick (the data event runs after current render finishes)
// before the next tick delivers the Enter.
function buildPasteBytes(input: string): string {
  return `\x1b[200~${input}\x1b[201~`;
}

const SUBMIT_BYTES = "\r";

// Delay between the paste write and the submit \r. We need to yield the event
// loop at least once so Claude's TUI processes the paste before Enter lands;
// a small real-time delay is more robust against the TUI batching renders
// across multiple data events. Kept tiny so user-perceived latency is nil.
const SUBMIT_DELAY_MS = 16;

// writeSubmit() polls in SUBMIT_DELAY_MS steps and only writes \r once the PTY
// has been quiet (no chunk) for a full SUBMIT_DELAY_MS — see writeSubmit() for
// the redraw race this closes. Cap the wait so a genuinely wedged/silent PTY
// still gets its \r rather than hanging forever.
const SUBMIT_MAX_WAIT_MS = 500;

// Build the environment for a spawned `claude` process. The Anthropic API key
// is injected only here — never exported into the streamer's global process
// env — so it does not leak into unrelated child processes. CLAUDE_API_KEY
// (the Fly secret) is mapped to ANTHROPIC_MODEL's sibling, ANTHROPIC_API_KEY,
// which the CLI reads. If CLAUDE_API_KEY is unset, nothing is added and the CLI
// falls back to its own auth (e.g. an interactive login).
function buildSpawnEnv(): Record<string, string> {
  const env = { ...process.env } as Record<string, string>;
  if (env.CLAUDE_API_KEY) {
    env.ANTHROPIC_API_KEY = env.CLAUDE_API_KEY;
  }
  // If the streamer itself was launched from inside a Claude Code session,
  // it inherits that session's markers (CLAUDECODE, CLAUDE_CODE_SESSION_ID,
  // CLAUDE_CODE_CHILD_SESSION, ...). A spawned `claude` seeing them treats
  // itself as a nested child session and never persists a project JSONL —
  // the session "works" in the terminal but has no conversation to stream.
  for (const key of Object.keys(env)) {
    if (key === "CLAUDECODE" || key.startsWith("CLAUDE_CODE_")) {
      delete env[key];
    }
  }
  // Claude Code reads TERM to decide whether the terminal can render its Unicode
  // chrome, and with TERM absent it paints ASCII fallbacks: `>` for the composer
  // prompt instead of `❯`, and `>` for the gate selection cursor. The composer
  // one is the damaging half — CLAUDE_PROMPT_MARKERS is `["╭", "❯"]`, so with
  // neither glyph ever painted `hasPromptMarker` is permanently false, a session
  // never leaves `running`, and mobile shows "Working" for a session that has
  // been idle for hours.
  //
  // TERM is absent here for the normal deployment, not an exotic one: a
  // supervised streamer (Task Scheduler / launchd / systemd) inherits no TERM of
  // its own, and node-pty's `name` option sets TERM on POSIX but does not reach
  // the child through Windows ConPTY. So the spawn declares `xterm-256color` in
  // `name` while the child sees no TERM at all — this makes the two agree.
  //
  // Measured A/B, same binary, same `name`, same cwd, only the env differing:
  // TERM absent -> the composer paints `> `; TERM=xterm-256color -> `❯ `.
  // (`WT_SESSION` flips it too, which is why a hand-run `claude` in Windows
  // Terminal never showed this and only the supervised spawn did.)
  if (!env.TERM) {
    env.TERM = "xterm-256color";
  }
  return env;
}

export class PTYManager implements SessionRunner {
  private sessions = new Map<string, InternalSession>();
  private onOutput: PTYManagerOptions["onOutput"];
  private onStatusChange: PTYManagerOptions["onStatusChange"];
  private onReady: PTYManagerOptions["onReady"];
  private onPermissionChange: PTYManagerOptions["onPermissionChange"];
  private onPhaseChange: PTYManagerOptions["onPhaseChange"];
  private onPromptSuggestionChange: PTYManagerOptions["onPromptSuggestionChange"];
  private onLiveQuestion: PTYManagerOptions["onLiveQuestion"];
  private onLiveQuestionGone: PTYManagerOptions["onLiveQuestionGone"];
  private onUserMessage: PTYManagerOptions["onUserMessage"];
  // Per-session permission-gate state: the gate currently open on a session's
  // screen. Set between an OSC 777 / paint-time claim (gate open) and the next
  // prompt-ready without a fresh 777 (gate closed). Prevents re-broadcasting
  // open/close on every chunk. Holds the gate itself rather than just the id so
  // sendKeys can tell whether the bytes it is writing are that gate's answer.
  private permissionOpen = new Map<string, PermissionGate>();
  // Last chunk's raw tail per session — prepended to the next chunk before
  // the OSC regex test so a split escape still matches. Consumed on match.
  private oscTail = new Map<string, string>();
  // Open turn per session, from OSC 9;4: "submitted" between our submit and
  // Claude's 9;4;3, "working" after it. Absent = no turn open.
  private turnOpen = new Map<string, "submitted" | "working">();
  // Sessions whose Claude has emitted OSC 9;4 at all — an older CLI that never
  // does keeps the marker-only behaviour instead of waiting on a signal.
  private progressSeen = new Set<string>();
  // An OSC 9;4 escape cut off at the end of the previous chunk, if any.
  private progressTail = new Map<string, string>();
  // When the last full detection pass ran per session (any trigger) — the
  // clock the SCRAPE_THROTTLE_MS ceiling is measured against.
  private lastDetectAt = new Map<string, number>();
  // Content key of the last AskUserQuestion broadcast from the rendered screen,
  // per session — de-dupes the same menu firing on consecutive repaints.
  private lastScreenQuestionKey = new Map<string, string>();
  // Content key of the last unstructured shell prompt (detectShellPrompt)
  // broadcast per session — present between open and resolve so we can clear it
  // on a prompt-ready/marker return and de-dupe consecutive repaints. Modelled
  // on permissionOpen but keyed by content (a shell prompt has no OSC trigger).
  private shellPromptOpen = new Map<string, string>();
  // Content key (permissionContentKey, cursor excluded — see arm 3) of the
  // gate that was still painted on screen when arm 2 closed it. Suppresses
  // arm 3 re-claiming that same box from a later trigger-less/throttled pass
  // before Claude erases it — otherwise the paint-time claim reopens the
  // card it just closed. Cleared once detectGateScreen sees the box is gone,
  // so a genuinely new (even content-identical) gate is claimed normally.
  private closedGateKey = new Map<string, string>();
  // Tracks sessions (both fresh and resume) whose PTY has spawned but Claude
  // hasn't yet reached an interactive prompt — i.e. onReady hasn't fired.
  private pendingReady = new Set<string>();
  // Inputs received via sendInput() while the session was still in pendingReady.
  // Flushed in arrival order once Claude reaches its first prompt. Without this,
  // input written into the raw PTY mid-boot is consumed by Claude's startup TUI
  // (welcome banner / first-run modals on fresh, JSONL restore on resume) and
  // silently lost — the "dot bug".
  private queuedInputs = new Map<string, string[]>();
  private log: Logger;
  // Timestamp of first PTY chunk per session; used for the [pty.ready] elapsed
  // measurement so a slow boot is visible in the logs.
  private firstChunkAt = new Map<string, number>();
  // Per-session chunk counter and last-chunk timestamp. Diagnostic-only,
  // feeds the [pty.chunk] log lines so we can trace whether Claude responded
  // to a given input or fell silent. Reset on dispose().
  private chunkIndex = new Map<string, number>();
  private lastChunkAt = new Map<string, number>();
  // Per-session debounced "went quiet" checker, re-armed on every chunk. Fires
  // QUIET_DETECT_MS after the last chunk so ready/prompt detection doesn't
  // wait for another chunk that may never arrive (Claude blocked on input).
  private quietCheckers = new Map<string, ReturnType<typeof debounce>>();
  // Per-session flat backstop from the first chunk (CLAUDE_READY_FALLBACK_MS).
  private readyFallbackTimers = new Map<string, NodeJS.Timeout>();
  // In-flight start()/startFresh() calls keyed by sessionId. A second
  // concurrent resume for the same session (double-tap, client retry) awaits
  // the first call's promise instead of spawning a duplicate PTY (CRITICAL #3).
  private startPromises = new Map<string, Promise<ManagedSession>>();

  constructor(options: PTYManagerOptions = {}) {
    this.onOutput = options.onOutput;
    this.onStatusChange = options.onStatusChange;
    this.onReady = options.onReady;
    this.onPermissionChange = options.onPermissionChange;
    this.onPhaseChange = options.onPhaseChange;
    this.onPromptSuggestionChange = options.onPromptSuggestionChange;
    this.onLiveQuestion = options.onLiveQuestion;
    this.onLiveQuestionGone = options.onLiveQuestionGone;
    this.onUserMessage = options.onUserMessage;
    this.log = options.logger ?? getLogger("pty");
  }

  // Resume an existing Claude conversation. sessionId is the JSONL UUID.
  //
  // options.permissionMode defaults to `acceptEdits` — the safe default that
  // auto-approves file edits while still prompting for shell commands. All six
  // Claude CLI modes are accepted (see PERMISSION_MODES in claude-flags.ts).
  //
  // On the bypass modes: `bypassPermissions`/`dontAsk` DO trigger a blocking
  // "Bypass Permissions mode" warning menu at boot ("1. No, exit" /
  // "2. Yes, I accept") which would strand the PTY and leave mobile on an empty
  // screen. buildSettingsJson() suppresses it by adding
  // `skipDangerousModePermissionPrompt` to the `--settings` blob for exactly
  // those modes — probe-verified on Claude Code v2.1.218. We never pass
  // `--dangerously-skip-permissions`; bypass is requested via --permission-mode.
  // (The other first-run gates — onboarding/theme, workspace trust,
  // custom-API-key — are cleared by the seeded ~/.claude.json in
  // docker/entrypoint.sh.) startFresh() uses the same default for the same reason.
  async start(sessionId: string, options: StartSessionOptions): Promise<ManagedSession> {
    // Guard the check-then-spawn: a second concurrent resume for the same
    // sessionId (double-tap, client retry — server.ts's own hasSession check
    // has an await gap before it calls start()) must not race past both
    // checks and spawn a second PTY. Returning the first call's in-flight
    // promise serializes the spawn; an already-running session short-circuits
    // without touching doStart at all.
    const existing = this.sessions.get(sessionId);
    if (existing) return toPublicSession(existing);

    const inFlight = this.startPromises.get(sessionId);
    if (inFlight) return inFlight;

    const promise = this.doStart(sessionId, options).finally(() => {
      this.startPromises.delete(sessionId);
    });
    this.startPromises.set(sessionId, promise);
    return promise;
  }

  private async doStart(sessionId: string, options: StartSessionOptions): Promise<ManagedSession> {
    const nodePty = await loadPty();
    const projectName = options.projectName ?? basename(options.projectPath);

    const permissionMode = options.permissionMode ?? "acceptEdits";
    const args = [
      "--permission-mode",
      permissionMode,
      "--settings",
      buildSettingsJson(permissionMode),
      "--model",
      options.model ?? "sonnet",
      "--effort",
      options.effort ?? "low",
      "--resume",
      sessionId,
    ];
    // Allowlisted per-server flags, then the free-text escape hatch — last so it
    // can override anything above it.
    args.push(...buildFlagArgs(options.claudeFlags, options.claudeExtraArgs));

    let proc: ReturnType<typeof nodePty.spawn>;
    try {
      proc = nodePty.spawn(resolveClaudeExe(), args, {
        name: "xterm-256color",
        cols: 120,
        rows: 40,
        cwd: options.projectPath,
        env: buildSpawnEnv(),
      });
    } catch (err) {
      // The cached path resolved to something CreateProcess can't launch
      // (stale after a reinstall, or a since-fixed bad match) — clear it so
      // the next start/resume attempt re-resolves instead of repeating the
      // same failure indefinitely.
      clearClaudeExeCache();
      throw err;
    }

    const session: InternalSession = {
      id: sessionId,
      provider: CLAUDE_CODE_PROVIDER,
      projectPath: options.projectPath,
      projectName,
      branch: options.branch ?? "",
      ...(options.effort != null && { effort: options.effort }),
      status: "running",
      statusSource: "spawn",
      statusUpdatedAt: new Date(),
      startedAt: new Date(),
      completedAt: null,
      promptCount: 0,
      lastOutput: "",
      process: proc,
      outputBuffer: Buffer.alloc(0),
      screen: createScreen(),
      inputHistory: [],
    };

    this.sessions.set(sessionId, session);
    // Resume re-uses the same boot path as a fresh launch: --resume replays the
    // JSONL into Claude's TUI, which can take several seconds before the prompt
    // is reachable. Until then, raw pty.write() bytes land in the boot UI and
    // are swallowed (the "dot bug" — first message vanishes, second message
    // appears to trigger both). Same pendingReady + flush gating as startFresh.
    this.pendingReady.add(sessionId);
    this.armReadyFallback(sessionId);

    proc.onData((data: string) => {
      this.handleOutput(sessionId, data);
    });

    proc.onExit(({ exitCode }: { exitCode: number }) => {
      this.pendingReady.delete(sessionId);
      this.handleExit(sessionId, exitCode);
    });

    return toPublicSession(session);
  }

  // Start a brand-new Claude session. A stable UUID is generated here and passed
  // to Claude via --session-id so the JSONL filename matches from the start.
  // onReady fires once Claude reaches its first prompt (waiting_input).
  async startFresh(options: StartFreshSessionOptions): Promise<ManagedSession> {
    const nodePty = await loadPty();
    const sessionId = randomUUID();
    const projectName = options.projectName ?? basename(options.projectPath);

    // `--permission-mode` defaults to acceptEdits for the same reason as start()
    // above. Guarded by __tests__/pty-ready-detection.test.ts.
    const permissionMode = options.permissionMode ?? "acceptEdits";
    const args = [
      "--permission-mode",
      permissionMode,
      "--settings",
      buildSettingsJson(permissionMode),
      "--model",
      options.model ?? "sonnet",
      "--effort",
      options.effort ?? "low",
      "--session-id",
      sessionId,
    ];
    if (options.systemPrompt) {
      args.push("--system-prompt", options.systemPrompt);
    }
    // Allowlisted per-server flags, then the free-text escape hatch — last so it
    // can override anything above it.
    args.push(...buildFlagArgs(options.claudeFlags, options.claudeExtraArgs));

    let proc: ReturnType<typeof nodePty.spawn>;
    try {
      proc = nodePty.spawn(resolveClaudeExe(), args, {
        name: "xterm-256color",
        cols: 120,
        rows: 40,
        cwd: options.projectPath,
        env: buildSpawnEnv(),
      });
    } catch (err) {
      // See the analogous catch in doStart() above.
      clearClaudeExeCache();
      throw err;
    }

    const session: InternalSession = {
      id: sessionId,
      provider: CLAUDE_CODE_PROVIDER,
      projectPath: options.projectPath,
      projectName,
      branch: "",
      ...(options.effort != null && { effort: options.effort }),
      status: "running",
      statusSource: "spawn",
      statusUpdatedAt: new Date(),
      startedAt: new Date(),
      completedAt: null,
      promptCount: 0,
      lastOutput: "",
      process: proc,
      outputBuffer: Buffer.alloc(0),
      screen: createScreen(),
      inputHistory: [],
    };

    this.sessions.set(sessionId, session);
    this.pendingReady.add(sessionId);
    this.armReadyFallback(sessionId);

    proc.onData((data: string) => {
      this.handleOutput(sessionId, data);
    });

    proc.onExit(({ exitCode }: { exitCode: number }) => {
      this.pendingReady.delete(sessionId);
      this.handleExit(sessionId, exitCode);
    });

    return toPublicSession(session);
  }

  // Write raw key bytes directly to the PTY without bracketed-paste wrapping.
  // Use for control sequences (arrow keys, Enter) that must not be quoted.
  sendKeys(sessionId: string, keys: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error(`Session not found: ${sessionId}`);
    if (session.status === "idle") {
      throw new Error(`Session is idle (no active PTY): ${sessionId}`);
    }
    if (session.status === "waiting_input") {
      session.status = "running";
      // Observed by construction: we are the ones writing the input.
      session.statusSource = "user-input";
      session.statusUpdatedAt = new Date();
      // Sending anything consumes the suggestion — don't wait for a scrape.
      this.setPromptSuggestion(sessionId, session, null);
      this.onStatusChange?.(toPublicSession(session));
    }
    this.log.info(`[pty.keys.write] ${sessionId.slice(0, 8)} bytes=${keys.length}`, {
      event: "pty.keys_write",
      sessionId,
      byteLen: keys.length,
    });
    session.process.write(keys);
    session.lastActivityAt = new Date();
    // These bytes answer the gate this session has open, so the box is gone as
    // soon as Claude reads them. The detector cannot see that: it closes on the
    // end-of-turn OSC or a prompt marker, and an approved tool run produces
    // neither until the whole turn ends — which left the card up for the rest of
    // the turn. Close it here, the one choke point every *card* answer goes
    // through, so `permissionOpen` and the server's `pendingPermission` retire
    // together (onPermissionChange(null) is what broadcasts permission_cancelled).
    //
    // NOT every way a gate gets answered: `/input` splits on the body field, so
    // a `{input}` composer send reaches sendInput() instead and still closes the
    // old way, at end of turn. That is deliberate — pattern-matching composer
    // prose into a gate answer is the false positive this rule exists to avoid.
    //
    // Optimistic by design: `closedGateKey` is deliberately NOT set here. If the
    // keystroke doesn't take and the box stays painted, the paint-time claim
    // reopens the card on the next pass — a gate that is still on screen must
    // never stay hidden. If it did take, the box is gone and nothing reclaims it.
    const openGate = this.permissionOpen.get(sessionId);
    if (openGate && isPermissionAnswer(openGate, keys)) {
      this.permissionOpen.delete(sessionId);
      this.onPermissionChange?.(sessionId, null);
    }
  }

  // Navigation keys move a focused picker without submitting user input. Keep
  // its waiting_input status intact so clients retain the prompt identity for
  // a multi-key sequence (for example Down, Down, Enter).
  sendRawKeys(sessionId: string, keys: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error(`Session not found: ${sessionId}`);
    if (session.status === "idle") throw new Error(`Session is idle (no active PTY): ${sessionId}`);
    this.log.info(`[pty.raw_keys.write] ${sessionId.slice(0, 8)} bytes=${keys.length}`, {
      event: "pty.raw_keys_write",
      sessionId,
      byteLen: keys.length,
    });
    session.process.write(keys);
    session.lastActivityAt = new Date();
  }

  sendInput(sessionId: string, input: string): number {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error(`Session not found: ${sessionId}`);
    if (session.status === "idle") {
      throw new Error(`Session is idle (no active PTY): ${sessionId}`);
    }
    // Claude is still booting (TUI not yet at first prompt). Writing into the
    // raw PTY now would let the startup UI swallow the keystrokes. Queue and
    // flush in flushQueuedInputs() once the prompt marker fires.
    if (this.pendingReady.has(sessionId)) {
      const queue = this.queuedInputs.get(sessionId) ?? [];
      queue.push(input);
      this.queuedInputs.set(sessionId, queue);
      session.lastActivityAt = new Date();
      session.promptCount++;
      // Surface that input is being held because Claude hasn't yet emitted a
      // prompt marker. If you see this without a corresponding pty.ready
      // follow-up, the marker detection has regressed.
      this.log.warn(
        `[pty.input.queued] ${sessionId.slice(0, 8)} promptCount=${session.promptCount} queueLen=${queue.length}`,
        {
          event: "pty.input_queued",
          sessionId,
          promptCount: session.promptCount,
          queueLen: queue.length,
          inputLen: input.length,
        },
      );
      return session.promptCount;
    }
    if (session.status === "waiting_input") {
      session.status = "running";
      // Observed by construction: we are the ones writing the input.
      session.statusSource = "user-input";
      session.statusUpdatedAt = new Date();
      // Sending anything consumes the suggestion — don't wait for a scrape.
      this.setPromptSuggestion(sessionId, session, null);
      this.onStatusChange?.(toPublicSession(session));
    }
    this.openTurnOnSubmit(sessionId);
    this.writeSubmit(sessionId, session, input, "direct", session.promptCount + 1);
    session.lastActivityAt = new Date();
    session.promptCount++;
    return session.promptCount;
  }

  // Two-step paste-then-submit. Writes the bracketed-paste body, then waits
  // for the PTY to go quiet before writing \r. See buildPasteBytes() for why
  // the split matters.
  //
  // The wait is quiescence-based, not a flat delay: a fixed SUBMIT_DELAY_MS
  // timer (the original fix) still races a TUI that's mid-redraw of its own
  // output (e.g. re-painting right after posting a question) when the paste
  // lands — the timer can elapse and fire \r while the TUI is still busy,
  // and that \r gets absorbed by the redraw instead of submitting (see
  // 2026-07 session 14dda340: a "Yes" reply was accepted into the input line
  // but never landed as a submitted JSONL turn). Polling in SUBMIT_DELAY_MS
  // steps and only submitting once lastChunkAt hasn't advanced for a full
  // step gives the TUI as many extra ticks as it needs, capped at
  // SUBMIT_MAX_WAIT_MS so a silent/wedged PTY still gets its \r eventually.
  private writeSubmit(
    sessionId: string,
    session: InternalSession,
    input: string,
    path: "direct" | "flush",
    promptCount: number,
  ): void {
    this.recordUserMessage(session, input);
    const pasteBytes = buildPasteBytes(input);
    this.log.info(
      `[pty.input.write] ${sessionId.slice(0, 8)} promptCount=${promptCount} bytes=${pasteBytes.length}`,
      {
        event: "pty.input_write",
        sessionId,
        promptCount,
        byteLen: pasteBytes.length,
        path,
        phase: "paste",
      },
    );
    const pasteAt = Date.now();
    session.process.write(pasteBytes);

    const trySubmit = () => {
      const current = this.sessions.get(sessionId);
      if (!current || current !== session) return;
      const now = Date.now();
      const lastChunk = this.lastChunkAt.get(sessionId) ?? pasteAt;
      const quiet = now - lastChunk >= SUBMIT_DELAY_MS;
      const timedOut = now - pasteAt >= SUBMIT_MAX_WAIT_MS;
      if (!quiet && !timedOut) {
        setTimeout(trySubmit, SUBMIT_DELAY_MS);
        return;
      }
      this.log.info(
        `[pty.input.submit] ${sessionId.slice(0, 8)} promptCount=${promptCount} digest=\\r waitedMs=${now - pasteAt} timedOut=${timedOut}`,
        {
          event: "pty.input_write",
          sessionId,
          promptCount,
          byteLen: SUBMIT_BYTES.length,
          digest: "\\r",
          path,
          phase: "submit",
          waitedMs: now - pasteAt,
          timedOut,
        },
      );
      current.process.write(SUBMIT_BYTES);
    };
    setTimeout(trySubmit, SUBMIT_DELAY_MS);
  }

  // Drain any inputs that were sent while the session was still pendingReady,
  // writing them in arrival order now that Claude is at its prompt.
  private flushQueuedInputs(sessionId: string): void {
    const queue = this.queuedInputs.get(sessionId);
    if (!queue || queue.length === 0) return;
    this.queuedInputs.delete(sessionId);
    const session = this.sessions.get(sessionId);
    if (!session) return;
    this.log.info(`[pty.flush] ${sessionId.slice(0, 8)} flushing ${queue.length} queued input(s)`, {
      event: "pty.flush_queued",
      sessionId,
      queueLen: queue.length,
    });
    // Chain queued inputs serially: each writeSubmit() defers its \r by
    // SUBMIT_DELAY_MS, and we further stagger subsequent inputs by 2x the
    // delay so paste/submit pairs don't interleave on the wire. Two queued
    // inputs is rare in practice (user tapped Send twice during the brief
    // boot window), but ordering must still produce two distinct submits.
    queue.forEach((input, i) => {
      const writeAt = i * SUBMIT_DELAY_MS * 2;
      if (writeAt === 0) {
        this.writeSubmit(sessionId, session, input, "flush", session.promptCount);
      } else {
        setTimeout(() => {
          const current = this.sessions.get(sessionId);
          if (!current || current !== session) return;
          this.writeSubmit(sessionId, session, input, "flush", session.promptCount);
        }, writeAt);
      }
    });
  }

  /**
   * Resize a live PTY. Silent when the session is unknown or has no process:
   * a terminal emitting SIGWINCH races session exit by nature, and throwing
   * would turn an ordinary window drag into an error the caller must catch.
   */
  resize(sessionId: string, cols: number, rows: number): void {
    const session = this.sessions.get(sessionId);
    if (!session?.process) return;
    if (!Number.isInteger(cols) || !Number.isInteger(rows) || cols < 1 || rows < 1) return;
    try {
      session.process.resize(cols, rows);
      // The headless render terminal MUST track the PTY (see pty-shared.ts):
      // getOutputLines reads its grid for replay, and the gate detectors scrape
      // it. Resizing one without the other desyncs every absolute cursor move
      // the TUI makes — which would break gate detection, the reason a managed
      // session exists at all.
      session.screen.resize(cols, rows);
    } catch (err) {
      // node-pty throws if the fd closed between the guard above and here.
      // The session is going away; a resize for it is moot, not an error.
      this.log.debug(`[pty.resize.failed] ${sessionId.slice(0, 8)}`, {
        event: "pty.resize_failed",
        sessionId,
        err,
      });
    }
  }

  cancel(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error(`Session not found: ${sessionId}`);
    session.process.kill("SIGINT");
  }

  killPid(pid: number): void {
    try {
      process.kill(pid, "SIGTERM");
    } catch {
      // Process may already be gone
    }
  }

  // Kill the PTY and mark the session idle. Called by the WS grace timer.
  // `signal` defaults to SIGINT (graceful); pass SIGKILL to force-kill a
  // process that isn't responding to it.
  putOnHold(sessionId: string, signal: NodeJS.Signals = "SIGINT"): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;
    this.pendingReady.delete(sessionId);
    this.queuedInputs.delete(sessionId);
    this.firstChunkAt.delete(sessionId);
    this.permissionOpen.delete(sessionId);
    this.oscTail.delete(sessionId);
    this.turnOpen.delete(sessionId);
    this.progressSeen.delete(sessionId);
    this.progressTail.delete(sessionId);
    this.lastDetectAt.delete(sessionId);
    this.lastScreenQuestionKey.delete(sessionId);
    this.shellPromptOpen.delete(sessionId);
    this.closedGateKey.delete(sessionId);
    this.quietCheckers.get(sessionId)?.cancel();
    this.quietCheckers.delete(sessionId);
    this.clearReadyFallback(sessionId);
    try {
      session.process.kill(signal);
    } catch {
      // already dead
    }
    session.status = "idle";
    session.statusSource = "shutdown";
    session.statusUpdatedAt = new Date();
    session.completedAt = new Date();
    session.screen.dispose();
    this.sessions.delete(sessionId);
    this.onStatusChange?.(toPublicSession(session));
  }

  getOutput(sessionId: string): string {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error(`Session not found: ${sessionId}`);
    return session.outputBuffer.toString("utf-8");
  }

  // Render the last `maxLines` rows of the session's screen in true on-screen
  // order. Reads the headless terminal (fed raw PTY bytes in handleOutput) so
  // Claude's absolute-cursor repaints resolve to where they actually paint —
  // unlike the old raw-byte slice, which scrambled order after a TUI repaint
  // and made replayed conversations appear out of order on resume.
  //
  // Async because xterm parses writes on a deferred tick; we flush pending
  // writes (empty write + callback) before reading so the buffer is current.
  async getOutputLines(sessionId: string, maxLines: number): Promise<string[]> {
    const session = this.sessions.get(sessionId);
    if (!session) throw new Error(`Session not found: ${sessionId}`);
    await new Promise<void>((resolve) => session.screen.write("", () => resolve()));

    const buf = session.screen.buffer.active;
    const lines: string[] = [];
    // buf.length spans scrollback + viewport; iterate the whole thing top-down
    // so the rendered output preserves screen order, then keep the last N.
    for (let y = 0; y < buf.length; y++) {
      lines.push(buf.getLine(y)?.translateToString(true) ?? "");
    }
    // Drop trailing blank rows (the unused bottom of the viewport) before
    // trimming to maxLines, so replay isn't padded with empty lines.
    while (lines.length > 0 && lines[lines.length - 1] === "") {
      lines.pop();
    }
    return lines.slice(-maxLines);
  }

  getInputHistory(sessionId: string): UserMessage[] {
    return this.sessions.get(sessionId)?.inputHistory ?? [];
  }

  // OS pid of the spawned agent, or null if the session isn't live here. The
  // durable registry records this so a later streamer run can probe whether the
  // process outlived it. Liveness alone is never identity — a recycled pid is
  // why the registry stores a cmdline alongside it.
  getPid(sessionId: string): number | null {
    return this.sessions.get(sessionId)?.process?.pid ?? null;
  }

  // Record a submitted user message as ground truth and fire onUserMessage.
  // Called from writeSubmit (both direct and flush paths) — never from
  // sendKeys, so raw keystrokes aren't logged as messages.
  private recordUserMessage(session: InternalSession, text: string): void {
    const ts = Date.now();
    session.inputHistory.push({ text, ts });
    if (session.inputHistory.length > INPUT_HISTORY_MAX) {
      session.inputHistory.shift();
    }
    // First message of the session: derive a title the same way the scanner
    // would once it observes this line, so a live session has a name from its
    // very first turn rather than waiting on a resume/rescan to compute it.
    if (session.firstMessageText === undefined) {
      session.firstMessageText = text;
      session.sessionName = deriveSessionName(text);
    }
    this.onUserMessage?.(session.id, text, ts);
  }

  getSession(sessionId: string): ManagedSession | null {
    const session = this.sessions.get(sessionId);
    return session ? toPublicSession(session) : null;
  }

  hasSession(sessionId: string): boolean {
    return this.sessions.has(sessionId);
  }

  listSessions(): ManagedSession[] {
    return Array.from(this.sessions.values()).map(toPublicSession);
  }

  dispose(): void {
    for (const session of this.sessions.values()) {
      try {
        session.process.kill();
      } catch {
        // Process may already be dead
      }
      session.screen.dispose();
    }
    this.sessions.clear();
    this.firstChunkAt.clear();
    this.chunkIndex.clear();
    this.lastChunkAt.clear();
    for (const quiet of this.quietCheckers.values()) quiet.cancel();
    this.quietCheckers.clear();
    for (const timer of this.readyFallbackTimers.values()) clearTimeout(timer);
    this.readyFallbackTimers.clear();
    this.permissionOpen.clear();
    this.oscTail.clear();
    this.turnOpen.clear();
    this.progressSeen.clear();
    this.progressTail.clear();
    this.lastDetectAt.clear();
    this.lastScreenQuestionKey.clear();
    this.shellPromptOpen.clear();
    this.closedGateKey.clear();
  }

  private handleOutput(sessionId: string, data: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    const chunk = Buffer.from(data, "utf-8");
    const now = Date.now();
    if (!this.firstChunkAt.has(sessionId)) {
      this.firstChunkAt.set(sessionId, now);
    }

    // Per-chunk diagnostic log. Keep until the @<path> submit bug is solved.
    const idx = (this.chunkIndex.get(sessionId) ?? 0) + 1;
    this.chunkIndex.set(sessionId, idx);
    const last = this.lastChunkAt.get(sessionId);
    this.lastChunkAt.set(sessionId, now);
    const gapMs = last == null ? 0 : now - last;
    this.log.info(
      `[pty.chunk] ${sessionId.slice(0, 8)} #${idx} +${chunk.length}B gap=${gapMs}ms status=${session.status}`,
      {
        event: "pty.chunk",
        sessionId,
        chunkIndex: idx,
        chunkBytes: chunk.length,
        gapMs,
        status: session.status,
        pendingReady: this.pendingReady.has(sessionId),
      },
    );

    session.outputBuffer = Buffer.concat([session.outputBuffer, chunk]);

    if (session.outputBuffer.length > OUTPUT_BUFFER_MAX) {
      session.outputBuffer = session.outputBuffer.subarray(
        session.outputBuffer.length - OUTPUT_BUFFER_MAX,
      );
    }

    // Render into the headless screen so getOutputLines() can reproduce true
    // on-screen order. write() is async (parsed on a later tick) but we never
    // read the screen synchronously after a single chunk — replay only happens
    // on subscribe, long after these writes have drained.
    session.screen.write(data);

    const stripped = stripAnsi(data);
    session.lastOutput = stripped;
    const matchedMarker = CLAUDE_PROMPT_MARKERS.find((m) => stripped.includes(m));
    const turnEnded = this.trackProgress(sessionId, data);

    if (session.status === "running" && matchedMarker && !this.turnOpen.has(sessionId)) {
      this.markReady(sessionId, session, "prompt-marker", `marker:${matchedMarker}`);
    }
    // The turn-end chunk rarely repaints the box, so ask the screen.
    if (turnEnded && session.status === "running") this.recheckReadyLogged(sessionId);
    // The no-marker backstop lives in armReadyFallback() rather than here: this
    // branch only ran when a chunk happened to arrive, so a boot that fell
    // silent before showing a marker was never rescued by it.

    this.onOutput?.(sessionId, data);

    // Live interactive-prompt detection from the PTY stream — fires the moment
    // a prompt is on screen, independent of (and ahead of) the JSONL flush.
    // Trigger-gated with a throttle floor so we don't scrape the rendered
    // buffer on every chunk:
    //   - OSC 777 (raw byte signal, tail-carried across chunks) → gate opened.
    //   - "Enter to select" footer (AskUserQuestion menu) → structured question.
    //   - an already-open gate/prompt keeps passing, so its close is seen (the
    //     prompt-ready marker without a fresh 777 is what marks it closed).
    //   - otherwise, at most every SCRAPE_THROTTLE_MS, a chunk alone triggers
    //     a pass — how a painted gate is claimed ~6s before its OSC arrives.
    this.detectLivePrompts(sessionId, data, stripped).catch((err) => {
      this.log.warn("[pty.prompt_detect] failed", {
        event: "pty.prompt_detect_failed",
        sessionId,
        err,
      });
    });

    // Re-arm the quiet-checker on every chunk. If no further chunk arrives for
    // QUIET_DETECT_MS, re-run the same ready/prompt detection without waiting
    // for a chunk that may never come (Claude blocked on a prompt).
    let quiet = this.quietCheckers.get(sessionId);
    if (!quiet) {
      quiet = debounce(() => this.handleQuiet(sessionId), QUIET_DETECT_MS);
      this.quietCheckers.set(sessionId, quiet);
    }
    quiet();
  }

  // Detect permission gates (OSC 777 + scraped options) and AskUserQuestion
  // menus from the rendered screen, firing the additive callbacks. Async because
  // reading the rendered buffer needs the xterm write queue flushed. Pure
  // detection lives in services/questions/*; this only orchestrates triggers,
  // per-session debounce, and the callbacks.
  private async detectLivePrompts(
    sessionId: string,
    rawData: string,
    stripped: string,
  ): Promise<void> {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    // OSC escapes can split across node-pty chunk boundaries — test against
    // the previous chunk's tail + this chunk. The tail carries the ROLLING
    // WINDOW (not just the last chunk), so a split across three+ chunks still
    // matches once the final piece arrives. The tail is consumed on a match
    // so the quiet path (rawData === "") can never re-fire a seen escape, and
    // left untouched by the quiet path so a pending partial isn't dropped.
    const oscWindow = (this.oscTail.get(sessionId) ?? "") + rawData;
    const oscPermission = hasPermissionOsc(oscWindow);
    // Claude finished its turn. Authoritative "no gate is open" signal — it
    // arrives even when no further chunk will (the turn is over), so it is the
    // only thing that can close a gate whose options never painted.
    const oscWaitingForInput = hasWaitingForInputOsc(oscWindow);
    if (rawData !== "") {
      if (oscPermission || oscWaitingForInput) this.oscTail.delete(sessionId);
      else this.oscTail.set(sessionId, oscWindow.slice(-OSC_TAIL_CHARS));
    }
    // Footer test on the CURRENT chunk — a cheap trigger only. The authoritative
    // test runs on the full rendered screen below (askFooterOnScreen), because
    // the OSC-777 notify and the "Enter to select" footer often arrive in
    // SEPARATE chunks: the OSC lands first (no footer yet), so a chunk-only test
    // misclassifies an AskUserQuestion menu as a permission gate.
    const hasAskFooter = /Enter to select/i.test(stripped);
    const hasPromptMarker = CLAUDE_PROMPT_MARKERS.some((m) => stripped.includes(m));
    // Cheap raw-chunk hint that an UNSTRUCTURED shell prompt may be on screen:
    // a y/N hint, a "press enter"/"continue?" tail, or a numbered menu row. Just
    // a trigger gate (mirrors hasAskFooter) — the real conservative matching is
    // in detectShellPrompt against the rendered tail. ponytail: substring gate.
    const hasShellPromptHint =
      /[[(]\s*y\s*\/\s*n\s*[\])]|press\s+(enter|return|any key)|\bcontinue\b\s*\?|^\s*(?:❯|>)?\s*\d+[.)]\s+\S/im.test(
        stripped,
      );

    // A suggestion is painted as SGR 2 (faint) after the turn's last chunk and
    // can land inside the throttle window of the previous pass; handleQuiet only
    // re-scans a `running` session, so without this trigger it would be missed
    // for good. Also keep scanning while one is shown so its clear is seen.
    const suggestionTrigger =
      session.status === "waiting_input" &&
      (rawData.includes("\x1b[2m") || session.promptSuggestion != null);

    // Paint-time throttle: when the last full pass is ≥ SCRAPE_THROTTLE_MS
    // old, a chunk's arrival alone is enough to scrape — the gate paint has
    // no reliable chunk-level signal and its OSC arrives ~6s late.
    const nowMs = Date.now();
    const scrapeDue = nowMs - (this.lastDetectAt.get(sessionId) ?? 0) >= SCRAPE_THROTTLE_MS;
    // Nothing to do unless a trigger fired or a prompt we already broadcast is
    // open (so we can detect its close on the next prompt-ready).
    if (
      !oscPermission &&
      !oscWaitingForInput &&
      !hasAskFooter &&
      !hasShellPromptHint &&
      !suggestionTrigger &&
      !scrapeDue &&
      !this.permissionOpen.has(sessionId) &&
      !this.shellPromptOpen.has(sessionId) &&
      !this.lastScreenQuestionKey.has(sessionId)
    ) {
      return;
    }
    this.lastDetectAt.set(sessionId, nowMs);

    const lines = await this.getOutputLines(sessionId, 60);

    // Phase refinement, off the screen read the detectors below already share.
    // Only while the turn is running: the phase describes what the agent is
    // doing *within* `running`, and markReady clears it at turn end. Reading it
    // in any other state would let a stale screen re-assert a phase after the
    // clear, which is the latch this design exists to prevent.
    const phaseSession = this.sessions.get(sessionId);
    if (phaseSession?.status === "running") {
      this.setPhase(sessionId, phaseSession, parseAgentPhase(lines, CLAUDE_CODE_PROVIDER));
    }

    // Composer suggestion, read from cell attributes off the same flushed screen
    // (getOutputLines awaited the xterm write queue). Not `waiting_input` -> no
    // suggestion, which also clears one left over from before a new turn.
    this.setPromptSuggestion(
      sessionId,
      session,
      session.status === "waiting_input" ? readPromptSuggestion(session.screen) : null,
    );

    // Authoritative footer test on the FULL rendered screen (not just the
    // trigger chunk). The "Enter to select · Tab/Arrow keys to navigate" footer
    // is unique to an AskUserQuestion menu; a permission gate uses "Tab to amend
    // · ctrl+e to explain". When this is on screen the prompt IS an
    // AskUserQuestion — even if an OSC-777 notify also fired — so it must take
    // priority over the permission path (Claude emits OSC 777 for BOTH).
    const askFooterOnScreen = lines.some((l) => /Enter to select/i.test(l));

    // Diagnostic: detector verdicts when a trigger fires. Metadata only — no
    // rendered lines, no scraped prompt/question text — so it stays safe at any
    // log level; prompt and answer content must never enter logs. At debug
    // level so it's silent under normal --verbose; enable with LOG_LEVEL=debug.
    if (oscPermission || hasAskFooter || askFooterOnScreen) {
      const permGate = oscPermission && !askFooterOnScreen ? scrapePermissionGate(lines) : null;
      const askQuestion = askFooterOnScreen ? detectQuestionFromScreen(lines) : null;
      this.log.debug?.(`[pty.prompt_detect] ${sessionId.slice(0, 8)} trigger`, {
        event: "pty.prompt_detect",
        sessionId,
        oscPermission,
        hasAskFooter,
        askFooterOnScreen,
        permGateDetected: permGate !== null,
        permGateOptionCount: permGate?.options.length ?? 0,
        askQuestionDetected: askQuestion !== null,
        askQuestionCount: askQuestion?.questions.length ?? 0,
      });
    }

    // ── Permission gate ──────────────────────────────────────────────
    // Skip entirely when the screen is an AskUserQuestion menu — its OSC-777
    // notify would otherwise be misread as a permission gate.
    if (oscPermission && !askFooterOnScreen) {
      const gate = scrapePermissionGate(lines);
      this.permissionOpen.set(sessionId, gate ?? { options: [] });
      // Broadcast even if options aren't painted yet (gate: empty options) so
      // the client can show "Claude needs permission" immediately; a later
      // repaint with the footer/options re-broadcasts the populated gate.
      this.onPermissionChange?.(sessionId, gate ?? { options: [] });
    } else if (this.permissionOpen.has(sessionId) && !askFooterOnScreen) {
      // Gate was open. If options are still on screen, refresh (cursor moved);
      // if the turn ended or the prompt is ready again with the options gone,
      // the gate closed. The end-of-turn notify is checked FIRST and without a
      // prompt-marker requirement: it is the last chunk of the turn, so a gate
      // still waiting on a marker here would never close at all.
      const gate =
        detectGateScreen(lines) ?? detectPickerScreen(lines) ?? detectStartupChoiceGate(lines);
      // "Gate is gone" must survive a mid-repaint tick: detectGateScreen needs
      // the footer, which can be briefly absent while the box repaints, and
      // hasPromptMarker matches the box's own ╭/❯ glyphs — together those would
      // close a live gate. Require BOTH detectors to see nothing before closing;
      // the refresh below stays strict so prose is never broadcast as options.
      const stillPainted = gate !== null || scrapePermissionGate(lines) !== null;
      if (oscWaitingForInput || (!stillPainted && hasPromptMarker)) {
        this.permissionOpen.delete(sessionId);
        this.onPermissionChange?.(sessionId, null);
        // Record what's still on screen (if anything) so arm 3 doesn't
        // reclaim this exact box on a later trigger-less pass before Claude
        // erases it — see closedGateKey.
        if (gate) {
          this.closedGateKey.set(sessionId, permissionContentKey({ ...gate, cursor: undefined }));
        } else {
          this.closedGateKey.delete(sessionId);
        }
      } else if (gate) {
        // Keep the stored gate the one actually on screen: sendKeys tests the
        // incoming bytes against its option indices, and a refresh can replace
        // the box with a different gate.
        this.permissionOpen.set(sessionId, gate);
        this.onPermissionChange?.(sessionId, gate);
      }
    } else if (!askFooterOnScreen && !oscWaitingForInput) {
      // Paint-time claim: the gate is on screen but Claude's OSC 777 notify
      // (debounced ~6s upstream) hasn't arrived. detectGateScreen anchors on
      // the gate footer + a Yes/No option label so a numbered list in prose
      // can't open a card; detectPickerScreen claims an unboxed picker with no
      // footer (a ❯ cursor and no composer below it). Downstream is the OSC
      // path unchanged — same broadcast, same dedupe, same close signals. A
      // still-painted box that arm 2 closed on an EARLIER pass reaches this
      // arm on the very next trigger-less/throttled tick (arm 3 is an else-if
      // of arm 2, so it can never run in the SAME pass as the close) —
      // closedGateKey suppresses reclaiming that exact content until Claude
      // erases the box.
      const gate =
        detectGateScreen(lines) ?? detectPickerScreen(lines) ?? detectStartupChoiceGate(lines);
      if (gate) {
        const key = permissionContentKey({ ...gate, cursor: undefined });
        if (this.closedGateKey.get(sessionId) !== key) {
          this.permissionOpen.set(sessionId, gate);
          this.onPermissionChange?.(sessionId, gate);
        }
      } else {
        // Box is gone — clear the suppression so a later, even
        // content-identical, gate is claimed normally.
        this.closedGateKey.delete(sessionId);
      }
    }

    // ── AskUserQuestion menu (rendered, ahead of JSONL) ──────────────
    // The multi-question TUI's final "Ready to submit your answers?" screen is
    // detected and broadcast as a normal card (Submit answers / Cancel) so the
    // user can tap to confirm — the carousel doesn't reliably auto-submit.
    // (Roadmap: robust carousel auto-submit.)
    if (askFooterOnScreen) {
      const detected = detectQuestionFromScreen(lines);
      if (detected) {
        const key = questionContentKey(detected.questions);
        if (this.lastScreenQuestionKey.get(sessionId) !== key) {
          this.lastScreenQuestionKey.set(sessionId, key);
          this.onLiveQuestion?.(sessionId, detected.questions);
        }
      }
    } else if (this.lastScreenQuestionKey.has(sessionId) && hasPromptMarker) {
      // The menu was open but its footer is gone and Claude's prompt marker is
      // back — the question was answered (or dismissed). Clear the screen key
      // and tell the server so the pending question is cancelled; without this
      // the answered menu lingers and a later repaint can re-broadcast it.
      this.lastScreenQuestionKey.delete(sessionId);
      this.onLiveQuestionGone?.(sessionId);
    }

    // ── Unstructured shell prompt (read -p "[y/N]", CLI picker, … ) ───
    // Last fallback: only when this isn't already a structured permission gate
    // or AskUserQuestion menu. Reuses the `permission` transport so mobile
    // renders a QuestionCard with zero new event handling; each option carries
    // its literal answerKeys (y\r / n\r / N\r) so the client stays dumb.
    if (!oscPermission && !askFooterOnScreen && !this.permissionOpen.has(sessionId)) {
      const shell = detectShellPrompt(lines);
      if (shell) {
        const key = `${shell.prompt}\u0000${shell.options.map((o) => o.label).join("\u0000")}`;
        if (this.shellPromptOpen.get(sessionId) !== key) {
          this.shellPromptOpen.set(sessionId, key);
          this.onPermissionChange?.(sessionId, {
            prompt: shell.prompt,
            options: shell.options,
          });
        }
      } else if (this.shellPromptOpen.has(sessionId) && hasPromptMarker) {
        // Prompt is gone and Claude's marker is back (input was answered) —
        // close the card.
        this.shellPromptOpen.delete(sessionId);
        this.onPermissionChange?.(sessionId, null);
      }
    }
  }

  // Fired QUIET_DETECT_MS after the last PTY chunk. Re-runs the same
  // ready/prompt detection handleOutput() runs per-chunk, using the last
  // rendered output — a session blocked on a prompt (or an unmarked boot
  // screen) may never produce another chunk to trigger detection otherwise.
  private handleQuiet(sessionId: string): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;
    if (session.status !== "running") {
      // A blocking startup gate (workspace trust) paints its own `❯` cursor on
      // the selected option, which readiness cannot tell from a composer prompt
      // — so the session settles to `waiting_input` at boot with the gate still
      // on screen. The screen then goes static, so the per-chunk paint-time
      // claim never runs again and the gate is never broadcast: it reaches the
      // client as raw terminal text with no way to answer it but raw keys.
      //
      // Scan once more here for exactly that case. Bounded the same way every
      // other unsolicited scrape is (the SCRAPE_THROTTLE_MS gate inside
      // detectLivePrompts), and skipped when a card is already open so an
      // answered gate is not re-claimed.
      if (session.status === "waiting_input" && !this.permissionOpen.has(sessionId)) {
        this.detectLivePrompts(sessionId, "", session.lastOutput).catch((err) => {
          this.log.warn("[pty.prompt_detect] idle rescan failed", {
            event: "pty.prompt_detect_failed",
            sessionId,
            err,
          });
        });
      }
      return;
    }

    if (this.pendingReady.has(sessionId)) {
      // Silence is NOT evidence of readiness while booting. `--resume` replays
      // the JSONL and can sit quiet for far longer than QUIET_DETECT_MS before
      // the TUI reads stdin at all; marking ready here disarmed the input
      // queue mid-boot, and the bytes then sat unconsumed in the tty buffer
      // until the TUI started reading — surfacing as the NEXT message silently
      // carrying the previous one as a prefix. Ask the screen instead, the
      // same question the settled branch below asks, and leave the flat
      // backstop (armReadyFallback) to break a genuine deadlock.
      this.recheckReadyFromScreen(sessionId).catch((err) => {
        this.log.warn("[pty.ready] boot screen recheck failed", {
          event: "pty.ready_recheck_failed",
          sessionId,
          err,
        });
      });
    } else {
      // handleOutput() only checks the triggering chunk for a prompt marker,
      // but Claude's TUI does differential repaints and doesn't always
      // retransmit the box border once it's already drawn — so a
      // mid-conversation return to idle can go undetected forever if the last
      // chunk didn't happen to carry it. Re-derive readiness from the actual
      // rendered screen instead of waiting for a chunk that may never come.
      this.recheckReadyFromScreen(sessionId).catch((err) => {
        this.log.warn("[pty.ready] screen recheck failed", {
          event: "pty.ready_recheck_failed",
          sessionId,
          err,
        });
      });
    }

    this.detectLivePrompts(sessionId, "", session.lastOutput).catch((err) => {
      this.log.warn("[pty.prompt_detect] failed", {
        event: "pty.prompt_detect_failed",
        sessionId,
        err,
      });
    });
  }

  /** Update the open turn from OSC 9;4 in this chunk; true when it just closed. */
  private trackProgress(sessionId: string, data: string): boolean {
    // Prepend an escape the previous chunk cut off, so a split one still counts.
    const window = (this.progressTail.get(sessionId) ?? "") + data;
    let state: string | undefined;
    for (const m of window.matchAll(CLAUDE_PROGRESS_RE)) state = m[1];
    // Carry only a trailing escape too short to be a whole `ESC]9;4;N` (7
    // chars), so a complete one is never counted twice.
    const esc = window.lastIndexOf("\x1b");
    const rest = esc >= 0 ? window.slice(esc) : "";
    if (rest.length < 7) this.progressTail.set(sessionId, rest);
    else this.progressTail.delete(sessionId);
    if (state === undefined) return false;
    this.progressSeen.add(sessionId);
    if (state !== "0") {
      this.turnOpen.set(sessionId, "working");
      return false;
    }
    return this.turnOpen.delete(sessionId);
  }

  private openTurnOnSubmit(sessionId: string): void {
    if (!this.progressSeen.has(sessionId)) return;
    this.turnOpen.set(sessionId, "submitted");
    const timer = setTimeout(() => {
      if (this.turnOpen.get(sessionId) !== "submitted") return;
      this.turnOpen.delete(sessionId);
      this.recheckReadyLogged(sessionId);
    }, TURN_START_GRACE_MS);
    timer.unref?.();
  }

  private recheckReadyLogged(sessionId: string): void {
    this.recheckReadyFromScreen(sessionId).catch((err) => {
      this.log.warn("[pty.ready] screen recheck failed", {
        event: "pty.ready_recheck_failed",
        sessionId,
        err,
      });
    });
  }

  // Re-check the rendered screen (not just the last chunk) for a prompt
  // marker. Only meaningful once pendingReady is already clear — the boot
  // fallback above covers the first prompt after spawn/resume. Scoped to a
  // full viewport (PTY_ROWS), not just the last few lines: "on screen" means
  // whatever a user attached to this PTY would currently see.
  private async recheckReadyFromScreen(sessionId: string): Promise<void> {
    const session = this.sessions.get(sessionId);
    if (session?.status !== "running") return;
    const lines = await this.getOutputLines(sessionId, PTY_ROWS);
    const matchedMarker = CLAUDE_PROMPT_MARKERS.find((m) => lines.some((l) => l.includes(m)));
    if (matchedMarker && session.status === "running" && !this.turnOpen.has(sessionId)) {
      this.markReady(sessionId, session, "screen-marker", `quiet:screen-marker:${matchedMarker}`);
    }
  }

  // Flat backstop from the first chunk: if neither a prompt marker nor the
  // screen recheck settles the session within CLAUDE_READY_FALLBACK_MS, mark it
  // ready anyway so start requests resolve and queued input is not held
  // forever. This is what makes the quiet-checker safe to be strict — a boot
  // variant whose marker we cannot see still recovers, just 8s later instead of
  // 500ms sooner and wrong. unref() so it never holds the process open.
  private armReadyFallback(sessionId: string): void {
    const timer = setTimeout(() => {
      this.readyFallbackTimers.delete(sessionId);
      const session = this.sessions.get(sessionId);
      if (session?.status === "running" && this.pendingReady.has(sessionId)) {
        this.markReady(sessionId, session, "timeout-fallback", "fallback:timeout");
      }
    }, CLAUDE_READY_FALLBACK_MS);
    timer.unref?.();
    this.readyFallbackTimers.set(sessionId, timer);
  }

  private clearReadyFallback(sessionId: string): void {
    const timer = this.readyFallbackTimers.get(sessionId);
    if (timer) clearTimeout(timer);
    this.readyFallbackTimers.delete(sessionId);
  }

  // Transition a session from "running" to "waiting_input", clear pendingReady,
  // and flush any queued input. Idempotent: callers can invoke at any chunk.
  /**
   * Record the agent's phase and notify only on a real change.
   *
   * The change guard is load-bearing, not an optimisation: the scrape pass runs
   * on every chunk, so an unguarded setter would fire the callback — and the
   * WS frame behind it — several times a second for the entire duration of a
   * turn while reporting the same value.
   */
  private setPhase(sessionId: string, session: InternalSession, phase: AgentPhase | null): void {
    const next = phase ?? null;
    if ((session.subStatus ?? null) === next) return;
    session.subStatus = next;
    this.onPhaseChange?.(sessionId, next);
  }

  /** Record the composer suggestion and notify only on a real change. */
  private setPromptSuggestion(
    sessionId: string,
    session: InternalSession,
    text: string | null,
  ): void {
    if ((session.promptSuggestion ?? null) === text) return;
    session.promptSuggestion = text;
    this.onPromptSuggestionChange?.(sessionId, text);
  }

  private markReady(
    sessionId: string,
    session: InternalSession,
    source: StatusSource,
    reason: string,
  ): void {
    this.clearReadyFallback(sessionId);
    session.lastActivityAt = new Date();
    session.status = "waiting_input";
    // C3: record HOW we concluded this, not just that we did. A
    // `timeout-fallback` here means no marker ever appeared and we assumed —
    // previously indistinguishable on the wire from an observed marker.
    session.statusSource = source;
    session.statusUpdatedAt = new Date();
    // Turn end clears the phase. This is the only place it can happen
    // correctly: the exit edge is not an output event, so it cannot be read off
    // the screen — Claude's TUI does differential repaints and a return to idle
    // can go undetected forever if the last chunk didn't carry the marker.
    // Without this the phase latches on any session that stops emitting, which
    // is the bug tb-mobile PR #647 shipped. markReady is the single idempotent
    // running -> waiting_input transition, and it is driven by the waiting-for-
    // input OSC, which arrives even when no further chunk will.
    // Kept below the source assignment so it stays inside the window
    // status-confidence.test.ts scans, and so onPhaseChange never observes a
    // session whose status has moved but whose statusSource has not.
    this.setPhase(sessionId, session, null);
    // Log retained on purpose: `reason=fallback:timeout` would be the only
    // signal that Claude's TUI introduced a new boot variant our markers miss.
    const elapsedMs = Date.now() - (this.firstChunkAt.get(sessionId) ?? Date.now());
    this.log.info(`[pty.ready] ${sessionId.slice(0, 8)} ${reason} (elapsed=${elapsedMs}ms)`, {
      event: "pty.ready",
      sessionId,
      reason,
      elapsedMs,
    });
    this.onStatusChange?.(toPublicSession(session));
    if (this.pendingReady.has(sessionId)) {
      this.pendingReady.delete(sessionId);
      this.flushQueuedInputs(sessionId);
      this.onReady?.(toPublicSession(session));
    }
  }

  private handleExit(sessionId: string, exitCode: number): void {
    const session = this.sessions.get(sessionId);
    if (!session) return;

    session.completedAt = new Date();
    session.status = "idle";
    session.statusSource = "process-exit";
    session.statusUpdatedAt = new Date();

    // Instant exit with no output — diagnose the most likely cause.
    const elapsedMs = session.completedAt.getTime() - session.startedAt.getTime();
    if (exitCode !== 0 && elapsedMs < 2000 && session.lastOutput === "") {
      if (!existsSync(session.projectPath)) {
        session.failureReason = `Project directory not found: ${session.projectPath}`;
      } else {
        session.failureReason =
          `Process exited immediately (code ${exitCode}). ` +
          `Check that the Claude binary is installed and accessible.`;
      }
    }

    this.onStatusChange?.(toPublicSession(session));
    session.screen.dispose();
    this.sessions.delete(sessionId);
    this.queuedInputs.delete(sessionId);
    this.firstChunkAt.delete(sessionId);
    this.permissionOpen.delete(sessionId);
    this.oscTail.delete(sessionId);
    this.turnOpen.delete(sessionId);
    this.progressSeen.delete(sessionId);
    this.progressTail.delete(sessionId);
    this.lastDetectAt.delete(sessionId);
    this.lastScreenQuestionKey.delete(sessionId);
    this.shellPromptOpen.delete(sessionId);
    this.closedGateKey.delete(sessionId);
    this.quietCheckers.get(sessionId)?.cancel();
    this.quietCheckers.delete(sessionId);
    this.clearReadyFallback(sessionId);
  }
}

function toPublicSession(s: InternalSession): ManagedSession {
  return {
    id: s.id,
    provider: s.provider ?? CLAUDE_CODE_PROVIDER,
    projectPath: s.projectPath,
    projectName: s.projectName,
    branch: s.branch,
    status: s.status,
    startedAt: s.startedAt,
    completedAt: s.completedAt,
    promptCount: s.promptCount,
    lastOutput: s.lastOutput,
    ...(s.failureReason != null && { failureReason: s.failureReason }),
    ...(s.lastActivityAt != null && { lastActivityAt: s.lastActivityAt }),
    ...(s.statusSource != null && { statusSource: s.statusSource }),
    ...(s.statusUpdatedAt != null && { statusUpdatedAt: s.statusUpdatedAt }),
    ...(s.filePath != null && { filePath: s.filePath }),
    ...(s.sessionName != null && { sessionName: s.sessionName }),
    ...(s.effort != null && { effort: s.effort }),
    ...(s.firstMessageText != null && { firstMessageText: s.firstMessageText }),
    // Unconditional: this shape crosses the pty-host boundary, and a streamer
    // re-adopting a surviving host's sessions mid-turn has no other source for
    // the phase — no snapshot or replay event carries it, and setPhase's change
    // guard means the host will never re-emit it for the rest of that turn.
    subStatus: s.subStatus ?? null,
    promptSuggestion: s.promptSuggestion ?? null,
  };
}
